import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {BenchlingTypeDef, LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  ArithmeticVariableType,
  LabAutomationTransform,
  LabAutomationTransformType,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {FilterType} from 'lookup-config/LookupConfigModalConstants';
import {RED_DARK} from 'ui-library/Colors';
import {assertNever, ElementKeyboardEvent, TextAreaChangeEvent} from 'util/Types';

const styles = StyleSheet.create({
  errorBorder: {
    border: `1px solid ${RED_DARK}`,
  },
});

export function updateTransformationProperties<
  T extends LabAutomationTransform & {transformationProperties?: object},
>(transform: T, key: string, value: unknown): T {
  return {
    ...transform,
    transformationProperties: {...transform.transformationProperties, [key]: value},
  } as unknown as T;
}

export function getColumnNamesFromInput(value: string): Array<string> {
  return value === '' ? [] : value.split(',').map((val) => val.trim());
}

function onCSVInputKeyDown(
  e: ElementKeyboardEvent,
  currentValue: Array<string | null>,
  updateValueFn: (value: Array<string | null>) => void
): void {
  if (e.key === 'Backspace') {
    if (currentValue[currentValue.length - 1] === '') {
      e.preventDefault();
      updateValueFn(currentValue.slice(0, currentValue.length - 1));
    }
  } else if (e.key === ' ') {
    e.preventDefault();
    const newValue = currentValue.slice(0, currentValue.length - 1);
    const lastItem = (currentValue[currentValue.length - 1] || '').concat(' ');
    newValue.push(lastItem);
    updateValueFn(newValue);
  }
}

export function CSVTextAreaInput(props: {
  disabled?: boolean;
  hasError?: boolean;
  value: Array<string | null> | undefined;
  placeholder?: string;
  onChange: (value: Array<string | null>) => void;
  parseCSVInputFn: (value: string) => Array<string | null>;
}): JSX.Element {
  return (
    <textarea
      className={css(props.hasError && styles.errorBorder)}
      disabled={props.disabled}
      rows={2}
      value={props.value?.join(', ')}
      placeholder={props.placeholder}
      onChange={(e: TextAreaChangeEvent) => {
        props.onChange(props.parseCSVInputFn(e.target.value));
      }}
      onKeyDown={(e: ElementKeyboardEvent) => {
        onCSVInputKeyDown(e, props.value || [], (newValue: Array<string | null>) => {
          props.onChange(newValue);
        });
      }}
    />
  );
}

export const SINGLE_VALUE_FILTER_TYPES = [
  FilterType.EQ,
  FilterType.NE,
  FilterType.LT,
  FilterType.LE,
  FilterType.GE,
  FilterType.GT,
];
export const MULTI_VALUE_FILTER_TYPES = [FilterType.ISIN, FilterType.NOTIN];

function isValidRegEx(value: string): boolean {
  try {
    RegExp(value);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    return false;
  }

  return true;
}
export interface AddColumnFormErrors {
  column: boolean;
  lookupConfig: boolean;
}

export interface ArithmeticFormErrors {
  operator: boolean;
  newColumnName: boolean;
  firstVariableType: boolean;
  firstVariableValue: boolean;
  secondVariableType: boolean;
  secondVariableValue: boolean;
  type: boolean;
}

export interface CustomTransformFormErrors {
  transformId: boolean;
}

export interface DuplicateFormErrors {
  newColumnName: boolean;
  columnToDuplicate: boolean;
}
export interface ExtraHeadersFormErrors {
  headerRowIndex: boolean;
  lastHeaderRowIndex: boolean;
}

export interface ExtraFootersFormErrors {
  numRowsToRemove: boolean;
}

export interface FilterFormErrors {
  type: boolean;
  column: boolean;
  value: boolean;
  values: boolean;
  lookupConfig: boolean;
}

export interface FindReplaceFormErrors {
  columns: boolean;
  findValue: boolean;
  replaceValue: boolean;
  lookupConfig: boolean;
  comparisonType: boolean;
}

export interface MergeFormErrors {
  columnNames: boolean;
  outputColumnName: boolean;
}

export interface MeltFormErrors {
  identifierColumns: boolean;
  valueColumns: boolean;
}

export interface PlateShapeFormErrors {
  plateSchema: boolean;
  firstWellIndex: boolean;
}

export interface PivotFormErrors {
  indexColumns: boolean;
  keysColumn: boolean;
  valuesColumn: boolean;
}

export interface SplitFormErrors {
  column: boolean;
  outputColumnNames: boolean;
  delimiter: boolean;
}

export interface EmptyFormErrors {
  transformType?: boolean;
}

export type FormErrors =
  | AddColumnFormErrors
  | ArithmeticFormErrors
  | CustomTransformFormErrors
  | DuplicateFormErrors
  | ExtraHeadersFormErrors
  | ExtraFootersFormErrors
  | FilterFormErrors
  | FindReplaceFormErrors
  | MergeFormErrors
  | MeltFormErrors
  | PlateShapeFormErrors
  | PivotFormErrors
  | SplitFormErrors
  | EmptyFormErrors;

export function validateTransform(
  transform: LabAutomationTransform,
  requireInputFileType: boolean = false
): FormErrors {
  function arrayHasDuplicates(arr: Array<unknown>): boolean {
    return new Set(arr).size !== arr.length;
  }
  switch (transform.transformType) {
    case LabAutomationTransformType.ADD_COLUMN:
      return {
        column: transform.transformationProperties.column == null,
        lookupConfig: transform.lookupConfig == null || transform.lookupConfig.lookupSteps.length === 0,
      };
    case LabAutomationTransformType.ARITHMETIC:
      return {
        operator: transform.transformationProperties.operator == null,
        newColumnName: transform.transformationProperties.newColumnName == null,
        firstVariableType: transform.transformationProperties.firstVariable?.type == null,
        firstVariableValue: transform.transformationProperties.firstVariable?.value == null,
        secondVariableType: transform.transformationProperties.secondVariable?.type == null,
        secondVariableValue: transform.transformationProperties.secondVariable?.value == null,
        type:
          transform.transformationProperties.firstVariable?.type === ArithmeticVariableType.CONSTANT &&
          transform.transformationProperties.secondVariable?.type === ArithmeticVariableType.CONSTANT,
      };
    case LabAutomationTransformType.CUSTOM:
      return {
        transformId: transform.transformationProperties.transformId == null,
      };
    case LabAutomationTransformType.DUPLICATE:
      return {
        columnToDuplicate: transform.transformationProperties.columnToDuplicate == null,
        newColumnName: transform.transformationProperties.newColumnName == null,
      };
    case LabAutomationTransformType.REMOVE_QUOTES:
      return {transformType: false};
    case LabAutomationTransformType.EXTRA_HEADERS: {
      const headerRowIndex = transform.transformationProperties.headerRowIndex;
      const lastHeaderRowIndex = transform.transformationProperties.lastHeaderRowIndex;
      if (lastHeaderRowIndex != null) {
        return {
          headerRowIndex: headerRowIndex == null || headerRowIndex < 0,
          lastHeaderRowIndex: headerRowIndex ? lastHeaderRowIndex <= headerRowIndex : false,
        };
      } else {
        return {
          headerRowIndex: headerRowIndex == null || headerRowIndex <= 0,
          lastHeaderRowIndex: false,
        };
      }
    }
    case LabAutomationTransformType.EXTRA_FOOTERS:
      return {
        numRowsToRemove:
          transform.transformationProperties.numRowsToRemove == null ||
          transform.transformationProperties.numRowsToRemove <= 0,
      };
    case LabAutomationTransformType.FILTER: {
      const filterType = transform.transformationProperties.type;
      return {
        type: filterType == null,
        column: transform.transformationProperties.column == null,
        value:
          transform.lookupConfig == null &&
          transform.transformationProperties.value == null &&
          filterType != null &&
          SINGLE_VALUE_FILTER_TYPES.includes(filterType),
        values:
          transform.lookupConfig == null &&
          (transform.transformationProperties?.values == null ||
            transform.transformationProperties.values.length === 0) &&
          filterType != null &&
          MULTI_VALUE_FILTER_TYPES.includes(filterType),
        lookupConfig:
          filterType != null &&
          (SINGLE_VALUE_FILTER_TYPES.includes(filterType) || MULTI_VALUE_FILTER_TYPES.includes(filterType)) &&
          transform.transformationProperties.value == null &&
          transform.transformationProperties.values == null &&
          (transform.lookupConfig == null || transform.lookupConfig.lookupSteps.length === 0),
      };
    }
    case LabAutomationTransformType.FIND_REPLACE: {
      const findValue = transform.transformationProperties.findValue;
      const replaceValue = transform.transformationProperties.replaceValue;

      return {
        columns:
          transform.transformationProperties.columns == null ||
          transform.transformationProperties.columns.length === 0,
        findValue:
          (findValue == null && transform.lookupConfig == null) ||
          (typeof findValue === 'string' && !isValidRegEx(findValue)),
        replaceValue: replaceValue == null && transform.lookupConfig == null,
        lookupConfig: transform.lookupConfig == null && (findValue == null || replaceValue == null),
        comparisonType: transform.transformationProperties.comparisonType == null,
      };
    }
    case LabAutomationTransformType.MERGE:
      return {
        columnNames:
          transform.transformationProperties.columnNames == null ||
          transform.transformationProperties.columnNames.length < 2,
        outputColumnName: transform.transformationProperties.outputColumnName == null,
      };
    case LabAutomationTransformType.MELT:
      return {
        identifierColumns:
          transform.transformationProperties.identifierColumns == null ||
          arrayHasDuplicates(transform.transformationProperties?.identifierColumns),
        valueColumns:
          transform.transformationProperties.valueColumns != null &&
          arrayHasDuplicates(transform.transformationProperties?.valueColumns),
      };
    case LabAutomationTransformType.PLATE_SHAPE:
      return {
        plateSchema: transform.transformationProperties.plateSchema == null,
        firstWellIndex:
          transform.transformationProperties.firstWellRowIndex == null ||
          transform.transformationProperties.firstWellColumnIndex == null,
      };

    case LabAutomationTransformType.PIVOT:
      return {
        indexColumns: transform.transformationProperties.indexColumns == null,
        keysColumn: transform.transformationProperties.keysColumn == null,
        valuesColumn: transform.transformationProperties.valuesColumn == null,
      };
    case LabAutomationTransformType.SPLIT:
      return {
        delimiter: transform.transformationProperties.delimiter == null,
        column: transform.transformationProperties.column == null,
        outputColumnNames:
          transform.transformationProperties.outputColumnNames == null ||
          transform.transformationProperties.outputColumnNames.filter((name) => name != null).length < 1,
      };
    case null:
    default:
      if (transform.transformType != null) {
        assertNever(transform);
      }
  }
  return {
    transformType: requireInputFileType,
  };
}

export function isFormErrorsEmpty(formErrors: FormErrors): boolean {
  return Object.values(formErrors).every((value) => !value);
}

export function isTransformValid(
  transform: LabAutomationTransform,
  allowEmptyType: boolean = false
): boolean {
  return isFormErrorsEmpty(validateTransform(transform, !allowEmptyType));
}

export function getInitialTransformOfType(type: LabAutomationTransformType): LabAutomationTransform {
  switch (type) {
    case LabAutomationTransformType.MERGE:
      return {
        transformType: type,
        transformationProperties: {
          delimiter: '',
        },
      };
    case LabAutomationTransformType.PLATE_SHAPE:
      return {
        transformType: type,
        transformationProperties: {
          deleteOtherCells: false,
        },
      };
    case LabAutomationTransformType.FIND_REPLACE:
      return {
        transformType: type,
        transformationProperties: {
          findValue: '',
          replaceValue: '',
        },
      };
    default:
      return {
        transformType: type,
        transformationProperties: {},
      };
  }
}

export function validateLookupIsNumeric(inferredType: InferredLookupConfigType): string | null {
  const numericTypes: Array<BenchlingTypeDef> = [LeafType.DECIMAL, LeafType.INTEGER];

  if (!numericTypes.includes(inferredType.typeDef)) {
    return 'Must look up a numeric value';
  }
  return null;
}
